import NextVisitService, {
  TNextVisitMarkType,
  type TPatientNextVisit,
} from "@/api/models/patient/nextVisit";
import { useGenericMutation } from "@/composables/shared/mutations/useGenericMutation";
import type { TVeeSetErrors } from "@/utils/DjangoError";
import { ref } from "vue";

interface InstructionOption {
  id: number;
  name: string;
  checked: boolean;
  description?: string;
}

export interface InstructionField {
  id: number;
  name: string;
  checked?: boolean;
  description?: string;
  options?: InstructionOption[];
}

export const useNextVisitInstructions = () => {
  const nextVisitInstructionsFields = ref<InstructionField[]>([
    {
      id: 1,
      name: "Review Oral Health Instructions",
      checked: false,
    },
    {
      id: 2,
      name: "Review weartime instructions",
      checked: false,
    },
    {
      id: 3,
      name: "Review rubber bands wear",
      checked: false,
    },
    {
      id: 4,
      name: "Review DM app",
      checked: false,
      options: [
        {
          id: 41,
          name: "Take first scan",
          checked: false,
        },
        {
          id: 42,
          name: "Review scan instructions",
          checked: false,
        },
      ],
    },
    {
      id: 5,
      name: "I/O scan update",
      checked: false,
    },
    {
      id: 6,
      name: "X-ray update",
      checked: false,
    },
    {
      id: 7,
      name: "Review attachments form",
      checked: false,
      options: [
        {
          id: 71,
          name: "Remove attachments",
          checked: false,
        },
        {
          id: 72,
          name: "Replace attachments",
          checked: false,
        },
      ],
    },
    {
      id: 8,
      name: "IPR review",
      checked: false,
    },
    {
      id: 9,
      name: "Manicure teeth",
      checked: false,
    },
    {
      id: 10,
      name: "Occlusal Adjustment",
      checked: false,
    },
    {
      id: 11,
      name: "Additional notes",
      description: "",
    },
  ]);

  const updateNextVisitInstructionsFields = (
    name: string,
    value: string | boolean,
    isNote = false
  ) => {
    const field = nextVisitInstructionsFields.value.find((f) => f.name === name);
    if (field) {
      if (isNote) {
        field.description = value as string;
      } else {
        field.checked = value as boolean;
        if (!value && field.options) {
          field.options.forEach((opt) => (opt.checked = false));
        }
      }
      return;
    }

    for (const field of nextVisitInstructionsFields.value) {
      if (field.options) {
        const option = field.options.find((opt) => opt.name === name);
        if (option) {
          if (isNote) {
            option.description = value as string;
          } else {
            field.options.forEach((opt) => (opt.checked = false));
            option.checked = value as boolean;
            field.checked = field.options.some((opt) => opt.checked);
          }
          return;
        }
      }
    }
  };

  return { nextVisitInstructionsFields, updateNextVisitInstructionsFields };
};

export function useNextVisitInstructionsMarkMutation(veeSetErrors?: TVeeSetErrors) {
  return useGenericMutation<TPatientNextVisit, { type: TNextVisitMarkType; id: string }>({
    mutationFn: async ({ type, id }) => NextVisitService.markAs({ nextVisit: id, type }, {}),
    feature: "nextVisitInstructions-mark-mutation",
    params: { veeSetErrors },
  });
}
